import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Download,
  Users,
  Clock,
  Shield,
  Smartphone,
  Laptop,
  Monitor,
  CheckCircle2,
  Zap,
  MessageCircle,
  Sparkles,
  HelpCircle,
  Mail,
  Phone,
} from "lucide-react";

interface AppData {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  features: string[];
  downloadLinks: {
    windows?: string;
    mac?: string;
    android?: string;
    ios?: string;
  };
  version: string;
  size: string;
  lastUpdated: string;
}

export const AppDownloads: React.FC = () => {
  const apps: AppData[] = [
    {
      id: "teamlio",
      name: "Teamlio",
      description:
        "Complete HR and payroll management solution. Manage employees, attendance, leaves, salaries, and generate reports.",
      icon: <Users className="h-8 w-8" />,
      color: "from-blue-500 to-indigo-600",
      features: [
        "Employee Management",
        "Attendance Tracking",
        "Leave Management",
        "Payroll Processing",
        "Tax Calculations",
        "Report Generation",
        "Salary Slips",
        "Performance Reviews",
      ],
      downloadLinks: {
        android: "https://api.teamlio.ai/download_hr_app/hr_payroll",
        ios: "https://apps.apple.com/app/hr-payroll/id123456789",
      },
      version: "3.2.1",
      size: "23 MB",
      lastUpdated: "2026-01-15",
    },
    {
      id: "work-monitor",
      name: "Work Flow",
      description:
        "Employee activity monitoring and productivity tracking. Track applications, websites, and work efficiency in real-time.",
      icon: <Clock className="h-8 w-8" />,
      color: "from-purple-500 to-pink-600",
      features: [
        "Activity Tracking",
        "Application Usage",
        "Website Monitoring",
        "Productivity Reports",
        "Screenshot Capture",
        "Idle Time Detection",
        "Real-time Dashboard",
        "Data Export",
      ],
      downloadLinks: {
        windows: "https://api.teamlio.ai/download_hr_app/work_monitor",
        mac: "https://example.com/work-monitor-mac.dmg",
      },
      version: "2.5.0",
      size: "225 MB",
      lastUpdated: "2026-03-24",
    },
    {
      id: "messenger",
      name: "TeamHub",
      description:
        "Real-time team messaging for Teamlio. Stay in sync with colleagues, share files, and collaborate across projects from your desktop.",
      icon: <MessageCircle className="h-8 w-8" />,
      color: "from-green-500 to-emerald-600",
      features: [
        "Direct Messaging",
        "Group Channels",
        "File Sharing",
        "Mentions & Reactions",
        "Meeting Scheduling",
        "Threaded Replies",
        "Desktop Notifications",
        "Cross-platform Sync",
      ],
      downloadLinks: {
        windows: "https://api.teamlio.ai/download_hr_app/messenger_windows",
        mac: "https://api.teamlio.ai/download_hr_app/messenger_mac",
      },
      version: "1.0.0",
      size: "173 MB (Win), 238 MB (Mac)",
      lastUpdated: "2026-06-01",
    },
  ];

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case "windows":
        return <Monitor className="h-4 w-4" />;
      case "mac":
        return <Laptop className="h-4 w-4" />;
      case "android":
        return <Smartphone className="h-4 w-4" />;
      case "ios":
        // Apple SVG glyph — lucide-react has no first-party Apple icon, so a
        // tiny inline path keeps the iOS button on-brand without an extra dep.
        return (
          <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            className="h-4 w-4"
            aria-hidden="true"
          >
            <path d="M16.365 1.43c0 1.14-.43 2.245-1.205 3.07-.83.89-2.17 1.585-3.275 1.5-.14-1.12.42-2.275 1.205-3.07.835-.86 2.27-1.555 3.275-1.5zM21 17.13c-.42.97-.62 1.41-1.16 2.27-.755 1.2-1.83 2.69-3.155 2.7-1.18.01-1.485-.78-3.095-.77-1.61.01-1.945.78-3.125.77-1.325-.01-2.345-1.36-3.1-2.56-2.11-3.39-2.33-7.37-1.025-9.49.93-1.51 2.41-2.39 3.795-2.39 1.41 0 2.295.78 3.46.78 1.13 0 1.82-.78 3.45-.78 1.23 0 2.535.68 3.46 1.86-3.04 1.67-2.55 6.04.495 7.61z" />
          </svg>
        );
      default:
        return <Download className="h-4 w-4" />;
    }
  };

  const getPlatformName = (platform: string) => {
    switch (platform) {
      case "windows":
        return "Windows";
      case "mac":
        return "macOS";
      case "android":
        return "Android";
      case "ios":
        return "iOS";
      default:
        return platform;
    }
  };

  return (
    <div className="gt-shell min-h-screen">
      <div className="max-w-7xl mx-auto p-4 md:p-7 space-y-8 gt-fade-in">
        {/* Header */}
        <div className="gt-page-hero flex-col md:flex-row md:items-end gap-4">
          <div className="min-w-0">
            <h1 className="gt-page-title">App <em>Downloads</em></h1>
            <p className="gt-page-sub" style={{ marginTop: 6 }}>
              Get the latest <em className="gt-serif-italic">Teamlio</em>,{" "}
              <em className="gt-serif-italic">Work Flow</em>, and{" "}
              <em className="gt-serif-italic">TeamHub</em> installers — served
              straight from cloud storage.
            </p>  
          </div>
          {/* Quick legend — colored dot per app for at-a-glance branding */}
          <div className="hidden md:flex items-center gap-3 text-xs text-slate-500 shrink-0">
            {apps.map((a) => (
              <div key={a.id} className="flex items-center gap-1.5">
                <span
                  className={`w-2 h-2 rounded-full bg-gradient-to-br ${a.color}`}
                />
                <span className="font-medium">{a.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Apps Grid — 3-up on wide screens, 2-up on tablet, stack on mobile.
            Each card has a colored ribbon, gradient icon backdrop, pill
            metadata, feature chips and full-width platform buttons that
            adopt the app's brand gradient on hover. */}
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {apps.map((app, idx) => (
            <article
              key={app.id}
              className="gt-app-card group relative flex flex-col rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden"
              style={{
                animation: 'gt-app-fade 480ms ease-out backwards',
                animationDelay: `${idx * 90}ms`,
              }}
            >
              {/* Brand ribbon — also serves as a hover hot-zone indicator */}
              <div
                className={`h-1.5 w-full bg-gradient-to-r ${app.color}`}
                aria-hidden
              />

              {/* Header — icon, name, version pill, description */}
              <header className="px-5 pt-5 pb-4 border-b border-slate-100">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div
                    className={`shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br ${app.color} text-white flex items-center justify-center shadow-md transition-transform duration-300 group-hover:scale-105 group-hover:-rotate-3`}
                  >
                    {app.icon}
                  </div>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-[10px] font-semibold text-emerald-700 tracking-wide uppercase whitespace-nowrap">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    v{app.version}
                  </span>
                </div>
                <h2 className="text-lg font-bold text-slate-900 leading-tight">
                  {app.name}
                </h2>
                <p className="mt-1.5 text-[13px] leading-relaxed text-slate-600 line-clamp-2 group-hover:line-clamp-none transition-all duration-200">
                  {app.description}
                </p>
                <div className="mt-3 flex items-center gap-3 text-[11px] text-slate-500 font-medium">
                  <span className="inline-flex items-center gap-1">
                    <Download className="w-3 h-3" /> {app.size}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-slate-300" />
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(app.lastUpdated).toLocaleDateString('en-GB')}
                  </span>
                </div>
              </header>

              {/* Feature chips — compact pills, capped at 5 + overflow count */}
              <div className="px-5 py-4 border-b border-slate-100 flex-1">
                <h3 className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                  Highlights
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {app.features.slice(0, 5).map((f) => (
                    <span
                      key={f}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-50 border border-slate-200 text-[10.5px] font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                    >
                      <Zap className="w-2.5 h-2.5 text-blue-500" />
                      {f}
                    </span>
                  ))}
                  {app.features.length > 5 && (
                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-slate-100 text-[10.5px] font-semibold text-slate-500">
                      +{app.features.length - 5} more
                    </span>
                  )}
                </div>
              </div>

              {/* Download buttons — fill with the app's gradient on hover */}
              <div className="px-5 py-4 bg-slate-50/60">
                <h3 className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                  <Download className="w-3.5 h-3.5 text-slate-600" />
                  Get the App
                </h3>
                {Object.keys(app.downloadLinks).length === 0 ? (
                  <p className="text-xs text-slate-400 italic">
                    No downloads available yet.
                  </p>
                ) : (
                  <div
                    className={`grid gap-2 ${
                      Object.keys(app.downloadLinks).length === 1
                        ? 'grid-cols-1'
                        : 'grid-cols-2'
                    }`}
                  >
                    {Object.entries(app.downloadLinks).map(
                      ([platform, link]) => (
                        <a
                          key={platform}
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group/btn relative overflow-hidden flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border border-slate-200 bg-white shadow-sm hover:border-transparent hover:shadow-lg hover:text-white text-slate-700 transition-all duration-300"
                        >
                          {/* Hover-fill gradient layer */}
                          <span
                            className={`absolute inset-0 opacity-0 group-hover/btn:opacity-100 bg-gradient-to-br ${app.color} transition-opacity duration-300`}
                            aria-hidden
                          />
                          <span className="relative flex items-center gap-2">
                            {getPlatformIcon(platform)}
                            <span className="text-xs font-semibold">
                              {getPlatformName(platform)}
                            </span>
                          </span>
                          <Download className="relative w-3.5 h-3.5 opacity-60 group-hover/btn:opacity-100 group-hover/btn:translate-y-0.5 transition-all duration-300" />
                        </a>
                      ),
                    )}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>

        {/* System Requirements — split into Desktop / Mobile panels with
            colored backdrops and per-line check icons. */}
        <Card className="border-0 shadow-lg overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                <Shield className="h-4 w-4 text-blue-600" />
              </div>
              System Requirements
            </CardTitle>
            <CardDescription>
              Make sure your machine meets these so the apps run smoothly.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-xl bg-gradient-to-br from-blue-50/70 to-white border border-blue-100/70 p-4">
                <h4 className="font-semibold text-sm mb-3 flex items-center gap-2 text-slate-800">
                  <span className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Laptop className="h-3.5 w-3.5 text-blue-600" />
                  </span>
                  Desktop
                </h4>
                <ul className="space-y-2 text-[13px] text-slate-600">
                  {[
                    "Windows 10/11 or macOS 11+",
                    "4 GB RAM minimum (8 GB recommended)",
                    "500 MB free disk space",
                    "Intel Core i3 or equivalent",
                    "Internet connection for sync",
                  ].map((line) => (
                    <li key={line} className="flex items-start gap-2">
                      <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-emerald-500 shrink-0" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl bg-gradient-to-br from-purple-50/70 to-white border border-purple-100/70 p-4">
                <h4 className="font-semibold text-sm mb-3 flex items-center gap-2 text-slate-800">
                  <span className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Smartphone className="h-3.5 w-3.5 text-purple-600" />
                  </span>
                  Mobile
                </h4>
                <ul className="space-y-2 text-[13px] text-slate-600">
                  {[
                    "Android 10+ or iOS 14+",
                    "2 GB RAM minimum",
                    "100 MB free storage",
                    "Stable internet connection",
                  ].map((line) => (
                    <li key={line} className="flex items-start gap-2">
                      <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-emerald-500 shrink-0" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Support — dark card with quick mail / phone buttons */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-6 md:p-8 shadow-xl">
          {/* Decorative blobs */}
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-blue-500/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-16 -left-12 w-56 h-56 rounded-full bg-purple-500/20 blur-3xl pointer-events-none" />

          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
            <div className="flex items-start gap-4 min-w-0">
              <div className="shrink-0 w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
                <HelpCircle className="w-6 h-6 text-blue-300" />
              </div>
              <div className="min-w-0">
                <h3 className="text-lg font-semibold">Need a hand getting set up?</h3>
                <p className="text-sm text-slate-300 mt-0.5">
                  Our team can walk you through installation or troubleshoot any
                  hiccup.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <a
                href="mailto:support@startupkhata.com"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white text-slate-900 text-sm font-semibold hover:bg-slate-100 transition-colors shadow-md"
              >
                <Mail className="w-4 h-4" />
                support@startupkhata.com
              </a>
              <a
                href="tel:+911140452748"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition-colors border border-white/15 backdrop-blur-sm"
              >
                <Phone className="w-4 h-4" />
                (011) 404-52-748
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Scoped keyframes — stagger the cards in on mount without pulling
          in framer-motion for a static, mostly-stationary page. */}
      <style>{`
        @keyframes gt-app-fade {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default AppDownloads;
